import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, loginWithGoogle } from '@/services/auth/authService';
import { useToast } from "@/hooks/use-toast";

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            // Login flow
            const response = await login({ email, password });
            
            if (response.success) {
                toast({
                    title: "Login successful",
                    description: "Welcome back to Spa Sense!",
                });
                // Navigate to dashboard after successful login
                navigate('/dashboard');
            } else {
                toast({
                    title: "Login failed",
                    description: "Please check your credentials and try again",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error('Login error:', error);
            toast({
                title: "Error",
                description: "An unexpected error occurred. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        
        try {
            const response = await loginWithGoogle();
            
            if (response.success) {
                toast({
                    title: "Login successful",
                    description: `Welcome back to Spa Sense, ${response.user.name || 'User'}!`,
                });
                // Navigate to dashboard after successful login
                navigate('/dashboard');
            } else {
                toast({
                    title: "Google login failed",
                    description: response.error || "Please try again or use email instead",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error('Google authentication error:', error);
            toast({
                title: "Error",
                description: "An unexpected error occurred. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const navigateToInviteCode = () => {
        navigate('/invite-code');
    };

    return (
        <div className="min-h-screen bg-brutal-white flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                <div className="brutal-card p-8">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-mono uppercase font-semibold text-brutal-black">
                            Login
                        </h2>
                        <p className="text-brutal-charcoal mt-2 font-mono">
                            Welcome back to Spa Sense
                        </p>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                                Email
                            </label>
                            <input 
                                id="email"
                                type="email" 
                                value={email} 
                                onChange={handleEmailChange} 
                                required 
                                className="brutal-input w-full"
                                placeholder="you@example.com"
                                disabled={isLoading}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-mono uppercase mb-2 text-brutal-black">
                                Password
                            </label>
                            <input 
                                id="password"
                                type="password" 
                                value={password} 
                                onChange={handlePasswordChange} 
                                required 
                                className="brutal-input w-full"
                                placeholder="••••••••"
                                disabled={isLoading}
                            />
                        </div>
                        <div>
                            <button 
                                type="submit" 
                                className="brutal-button w-full"
                                disabled={isLoading}
                            >
                                {isLoading ? "Logging in..." : "Login"}
                            </button>
                        </div>
                    </form>
                    
                    {/* Google authentication */}
                    <div className="mt-8">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t-4 border-brutal-black"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-brutal-white text-brutal-black font-mono uppercase">
                                    Or continue with
                                </span>
                            </div>
                        </div>
                        
                        <div className="mt-6">
                            <button 
                                onClick={handleGoogleLogin}
                                className="brutal-button w-full bg-white text-brutal-black border-brutal-black hover:bg-gray-100 flex items-center justify-center"
                                type="button"
                                disabled={isLoading}
                            >
                                <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="mr-2">
                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                                    <path fill="none" d="M0 0h48v48H0z"/>
                                </svg>
                                {isLoading ? "Processing..." : "Login with Google"}
                            </button>
                        </div>
                    </div>
                    
                    <div className="mt-8 text-center">
                        <p className="text-brutal-charcoal">
                            Don't have an account?{" "}
                            <button
                                type="button"
                                onClick={navigateToInviteCode}
                                className="text-brutal-dark font-bold hover:underline"
                                disabled={isLoading}
                            >
                                Sign up
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
